/**
 * Clip window rules, mirroring api/RosterPilotAPI/Constants/VideoClipRules.cs.
 *
 * Keep these in sync with the server. The point is that a clip the user can build in the
 * editor is always one the API will accept — validation here is for feedback, not security.
 */

/** Shortest clip the API will store. */
export const MIN_CLIP_DURATION_SECONDS = 0.5;

/** Most clips the API accepts in a single save. */
export const MAX_CLIPS_PER_REQUEST = 20;

/**
 * Formats a duration for display as m:ss (or h:mm:ss past an hour).
 * Negative and non-finite inputs collapse to 0:00 rather than rendering "NaN:aN".
 */
export const formatClipTime = (seconds: number): string => {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';

  const total = Math.floor(seconds);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = total % 60;

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${minutes}:${secs.toString().padStart(2, '0')}`;
};

/**
 * Validates one clip window against the source duration.
 * Returns a user-facing message, or null when the window is acceptable.
 */
export const validateClipWindow = (
  startSeconds: number,
  endSeconds: number,
  durationSeconds: number
): string | null => {
  if (!Number.isFinite(startSeconds) || !Number.isFinite(endSeconds)) {
    return 'Clip start and end must be valid times.';
  }
  if (startSeconds < 0) {
    return 'Clip cannot start before the beginning of the video.';
  }
  if (endSeconds <= startSeconds) {
    return 'Clip must end after it starts.';
  }
  if (endSeconds - startSeconds < MIN_CLIP_DURATION_SECONDS) {
    return `Clip must be at least ${MIN_CLIP_DURATION_SECONDS} seconds long.`;
  }
  if (Number.isFinite(durationSeconds) && durationSeconds > 0 && startSeconds >= durationSeconds) {
    return 'Clip starts after the end of the video.';
  }
  return null;
};
